/**
 * Drive-folder importer for the dock-mission planner.
 *
 * Single entry point:
 *   - `importMockMissionData` — enumerates a Drive folder, categorizes the
 *     files, imports thermal photos (R2 upload for EXIF GPS), pairs visual/
 *     zoom siblings, lists videos, parses an SRT into a flight path. Returns
 *     shaped data ready to drop into dockDemoStore via `setMissionImportedData`.
 *
 * Invoked from the user-triggered `DriveMissionImportModalDemo` ("Import
 * mission data" button on the mission planner panel).
 */

import {
  listDriveFolder,
  importDriveFile,
  type DriveFileImportResult,
  type DriveSrtResult,
} from '../services/assetApi';
import {
  categorizeFiles,
  matchPhotoSets,
  matchFlightPaths,
  driveThumbnailUrl,
  driveVideoEmbedUrl,
  type PhotoSet,
} from './driveImport';
import { parseSrt } from './srt';
import type {
  DemoPhoto,
  DemoVideo,
  DemoFlightPath,
} from '../components/dock-demo/types';

function genId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export interface MockImportResult {
  photos: DemoPhoto[];
  videos: DemoVideo[];
  flightPath: DemoFlightPath | null;
}

export interface MockImportProgress {
  /** Human-readable description of the current step. */
  step: string;
  /** Optional 0..1 progress for the current phase. */
  fraction?: number;
}

/**
 * Run the full demo Drive import. Returns the assembled photos / videos /
 * flight path. Throws on listing failure or critical import errors —
 * caller is responsible for surfacing the error to the user.
 *
 * Implementation is intentionally tolerant: photos without EXIF GPS are
 * skipped (the renderer would place them at null island anyway), missing
 * SRT is fine (flightPath returns null).
 */
export async function importMockMissionData(
  siteId: string,
  folderUrl: string,
  onProgress?: (p: MockImportProgress) => void,
): Promise<MockImportResult> {
  onProgress?.({ step: 'Listing Drive folder…' });
  const res = await listDriveFolder(siteId, folderUrl);
  const categorized = categorizeFiles(res.files);
  const photoSets = matchPhotoSets(categorized);
  const flightPaths = matchFlightPaths(categorized);

  const photos: DemoPhoto[] = [];
  const videos: DemoVideo[] = [];
  let flightPath: DemoFlightPath | null = null;

  // Build a quick lookup of thermal → visual/zoom siblings. Unused in the
  // current import shape (we add all photos individually), but kept here
  // so future "promote thermal pin to carry siblings" behaviour is easy
  // to wire in.
  const photoSetByThermalId = new Map<string, PhotoSet>();
  for (const ps of photoSets) {
    if (ps.thermal) photoSetByThermalId.set(ps.thermal.driveFile.id, ps);
  }

  // Step 1 — thermal photos: real import (R2 upload) for the EXIF GPS.
  const thermalFiles = categorized.filter((f) => f.category === 'thermal_photo');
  for (let i = 0; i < thermalFiles.length; i++) {
    const f = thermalFiles[i];
    onProgress?.({
      step: `Importing photo ${i + 1} / ${thermalFiles.length}: ${f.driveFile.name}`,
      fraction: (i + 1) / Math.max(1, thermalFiles.length),
    });
    const result = (await importDriveFile(
      siteId,
      f.driveFile.id,
      f.driveFile.name,
      'photo',
    )) as DriveFileImportResult;
    if (!result.exif?.lat || !result.exif?.lng) continue; // skip GPS-less photos
    photos.push({
      id: genId('photo'),
      fileName: f.driveFile.name,
      category: 'thermal_photo',
      driveFileId: f.driveFile.id,
      displayUrl: result.url,
      coordinates: [result.exif.lng, result.exif.lat],
      altitude: result.exif.altitude,
      bearing: result.exif.bearing,
      captureDate: result.exif.captureDate,
    });
  }

  // Step 2 — visual / zoom siblings: Drive proxy (no R2 upload). Placed at
  // [0,0] since they have no EXIF GPS; DemoMissionMapLayer's filter drops
  // null-island entries from the on-map renderer but they still surface
  // in the photo viewer.
  for (const f of categorized) {
    if (f.category !== 'visual_photo' && f.category !== 'zoom_photo') continue;
    photos.push({
      id: genId('photo'),
      fileName: f.driveFile.name,
      category: f.category,
      driveFileId: f.driveFile.id,
      displayUrl: driveThumbnailUrl(f.driveFile.id, 2000),
      coordinates: [0, 0],
    });
  }

  // Step 3 — videos: just record embed URLs.
  for (const f of categorized) {
    if (f.category !== 'video') continue;
    videos.push({
      id: genId('video'),
      fileName: f.driveFile.name,
      driveFileId: f.driveFile.id,
      embedUrl: driveVideoEmbedUrl(f.driveFile.id),
    });
  }

  // Step 4 — flight path from SRT.
  const fps = flightPaths[0];
  if (fps?.thermalSrt) {
    onProgress?.({ step: `Parsing SRT: ${fps.thermalSrt.driveFile.name}` });
    const srtRes = (await importDriveFile(
      siteId,
      fps.thermalSrt.driveFile.id,
      fps.thermalSrt.driveFile.name,
      'srt',
    )) as DriveSrtResult;
    const parsed = parseSrt(srtRes.content, srtRes.fileName);
    if (parsed.coordinates.length > 0) {
      flightPath = {
        id: genId('fp'),
        name: parsed.name,
        coordinates: parsed.coordinates,
        timestamps: parsed.timestamps,
        thermalEmbedUrl: fps.thermalVideo
          ? driveVideoEmbedUrl(fps.thermalVideo.driveFile.id)
          : undefined,
        visualEmbedUrl: fps.visualVideo
          ? driveVideoEmbedUrl(fps.visualVideo.driveFile.id)
          : undefined,
      };
    }
  }

  onProgress?.({ step: 'Done.' });
  return { photos, videos, flightPath };
}
